import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { DsoPoint } from '../../hooks/useKPIs'

interface DsoTrendChartProps {
  data: DsoPoint[]
}

// "2025-01" → "Gen '25"
function formatMonth(month: string): string {
  const [year, m] = month.split('-')
  const MONTHS = ['Gen', 'Feb', 'Mar', 'apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
  const label = MONTHS[parseInt(m, 10) - 1] ?? m
  return `${label} '${year.slice(2)}`
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean
  payload?: Array<{ value: number }>
  label?: string
}) {
  if (!active || !payload?.length) return null
  const dso = payload[0].value
  return (
    <div className="rounded-xl border border-[--km-border] bg-[#141b28] px-4 py-3 shadow-xl text-sm">
      <p className="text-[--km-text-secondary] mb-1 font-medium">{label}</p>
      <p className="text-[--km-text-primary] font-semibold tabular-nums">
        {dso.toFixed(0)} giorni
      </p>
      <p className={`text-xs mt-0.5 ${dso <= 30 ? 'text-emerald-400' : dso <= 60 ? 'text-amber-400' : 'text-red-400'}`}>
        {dso <= 30 ? 'ottimo' : dso <= 60 ? 'nella norma' : 'critico'}
      </p>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 py-10">
      <p className="text-sm text-[--km-text-muted]">Nessun dato DSO disponibile.</p>
      <p className="text-xs text-[--km-text-muted]">Carica le fatture per visualizzare il trend.</p>
    </div>
  )
}

export function DsoTrendChart({ data }: DsoTrendChartProps) {
  if (!data.length) return <EmptyState />

  const chartData = data.map((d) => ({ ...d, mese: formatMonth(d.month) }))

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={chartData} margin={{ top: 16, right: 16, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id="gradDso" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
          </linearGradient>
        </defs>

        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />

        <XAxis
          dataKey="mese"
          tick={{ fill: 'var(--km-text-muted)', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          dy={8}
          interval="preserveStartEnd"
        />

        <YAxis
          tickFormatter={(v) => `${v}gg`}
          tick={{ fill: 'var(--km-text-muted)', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          width={40}
        />

        <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }} />

        {/* Reference lines per soglie DSO */}
        <ReferenceLine
          y={30}
          stroke="#10b981"
          strokeDasharray="4 4"
          strokeOpacity={0.5}
          label={{ value: '30gg', fill: '#10b981', fontSize: 10, position: 'right' }}
        />
        <ReferenceLine
          y={60}
          stroke="#f59e0b"
          strokeDasharray="4 4"
          strokeOpacity={0.5}
          label={{ value: '60gg', fill: '#f59e0b', fontSize: 10, position: 'right' }}
        />

        <Area
          type="monotone"
          dataKey="dso"
          name="DSO"
          stroke="#6366f1"
          strokeWidth={2}
          fill="url(#gradDso)"
          dot={false}
          activeDot={{ r: 5, fill: '#6366f1', stroke: '#fff', strokeWidth: 1.5 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}
